import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BarChart3, Check, Pencil, Rocket, Send, User, X } from 'lucide-react';
import { chatApi } from '../services/api';
import { useRefresh } from '../contexts/RefreshContext';
import { BrandWordmark } from './BrandLogo';
import { BrandMark } from './BrandLogo';

const SUGGESTIONS = [
  'Post a job for Python developer in Mohali',
  'Show all my job postings',
  'How many candidates applied for Tester?',
  'Show selected candidates for React Developer',
  'Update resume threshold to 70 for Python Developer',
];

function renderMarkdown(text) {
  // Minimal markdown: **bold**, bullet lines, line breaks
  return text
    .split('\n')
    .map((line, i) => {
      const parts = line.split(/(\*\*[^*]+\*\*)/g).map((part, j) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={j}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      const isBullet = line.trimStart().startsWith('•') || line.trimStart().startsWith('-');
      return (
        <div key={i} style={{ marginBottom: isBullet ? 3 : 2, paddingLeft: isBullet ? 4 : 0 }}>
          {parts}
        </div>
      );
    });
}

function ActionCard({ action, onNavigate }) {
  if (!action) return null;
  const { type, job_id, title } = action;

  if (type === 'job_created') {
    return (
      <div style={{
        marginTop: 8, padding: '8px 12px',
        background: 'rgba(63,185,80,0.1)', border: '1px solid rgba(63,185,80,0.3)',
        borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}><Rocket size={12} strokeWidth={2} /> {title} — Live now</span>
        <button
          onClick={() => onNavigate(`/jobs/${job_id}`)}
          style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, background: 'var(--green)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >View Job <ArrowRight size={11} strokeWidth={2.4} /></button>
      </div>
    );
  }
  if (type === 'job_published') {
    return (
      <div style={{
        marginTop: 8, padding: '8px 12px',
        background: 'rgba(88,166,255,0.1)', border: '1px solid rgba(88,166,255,0.3)',
        borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}><Rocket size={12} strokeWidth={2} /> {title} is now Live</span>
        <button
          onClick={() => onNavigate(`/jobs/${job_id}`)}
          style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >View <ArrowRight size={11} strokeWidth={2.4} /></button>
      </div>
    );
  }
  if (type === 'job_updated') {
    return (
      <div style={{
        marginTop: 8, padding: '8px 12px',
        background: 'rgba(210,153,34,0.1)', border: '1px solid rgba(210,153,34,0.3)',
        borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 12, color: 'var(--yellow)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}><Pencil size={12} strokeWidth={2} /> {title} updated</span>
        <button
          onClick={() => onNavigate(`/jobs/${job_id}`)}
          style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, background: 'var(--yellow)', color: '#000', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >View <ArrowRight size={11} strokeWidth={2.4} /></button>
      </div>
    );
  }
  if (type === 'list_jobs' && action.jobs?.length) {
    return (
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {action.jobs.slice(0, 5).map(j => (
          <button
            key={j.id}
            onClick={() => onNavigate(`/jobs/${j.id}`)}
            style={{
              textAlign: 'left', padding: '5px 10px', borderRadius: 6,
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              cursor: 'pointer', fontSize: 12, color: 'var(--accent)', fontWeight: 500,
            }}
          >
            {j.title} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>— {j.status}</span>
          </button>
        ))}
      </div>
    );
  }
  if (type === 'job_stats') {
    return (
      <div style={{
        marginTop: 8, padding: '8px 12px',
        background: 'rgba(88,166,255,0.08)', border: '1px solid rgba(88,166,255,0.2)',
        borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
          <BarChart3 size={12} strokeWidth={2} /> {title} — {action.total} applicants · {action.shortlisted} selected
        </span>
        <button
          onClick={() => onNavigate(`/jobs/${job_id}`)}
          style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >View <ArrowRight size={11} strokeWidth={2.4} /></button>
      </div>
    );
  }
  if (type === 'list_candidates' && action.candidates?.length) {
    return (
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {action.candidates.slice(0, 5).map(c => (
          <button
            key={c.id}
            onClick={() => onNavigate(`/candidates/${c.id}`)}
            style={{
              textAlign: 'left', padding: '5px 10px', borderRadius: 6,
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)', fontWeight: 500,
            }}
          >
            {c.name || 'Unknown'}
            {c.shortlisted && <span style={{ color: 'var(--green)', marginLeft: 6, fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 3 }}><Check size={10} strokeWidth={3} /> Selected</span>}
          </button>
        ))}
      </div>
    );
  }
  if (type === 'view_candidate') {
    return (
      <div style={{
        marginTop: 8, padding: '8px 12px',
        background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.2)',
        borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 12, color: '#a78bfa', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}><User size={12} strokeWidth={2} /> Candidate Profile</span>
        <button
          onClick={() => onNavigate(`/candidates/${action.candidate_id}`)}
          style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, background: '#a78bfa', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >View <ArrowRight size={11} strokeWidth={2.4} /></button>
      </div>
    );
  }
  return null;
}

export default function ChatWidget() {
  const navigate = useNavigate();
  const { triggerRefresh } = useRefresh();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content, action?}
  const [chatContext, setChatContext] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }
  }, [messages, open]);

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg = { role: 'user', content: msg };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setLoading(true);

    try {
      const apiMessages = nextMessages.map(m => ({ role: m.role, content: m.content }));
      const result = await chatApi.send(apiMessages, chatContext);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: result.response,
        action: result.action || null,
      }]);
      if (result.context) {
        setChatContext(result.context);
      }
      // Re-fetch all pages that depend on job/candidate data
      if (result.action) triggerRefresh();
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
        action: null,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    setOpen(false);
  };

  const clearChat = () => {
    setMessages([]);
    setChatContext(null);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
          height: 50,
          padding: open ? '0 18px' : '0 20px 0 14px',
          borderRadius: 50,
          background: open
            ? 'var(--bg-card)'
            : 'linear-gradient(135deg, #640d5f 0%, #d91656 100%)',
          border: open ? '2px solid var(--border)' : 'none',
       
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 9,
          transition: 'all 0.25s cubic-bezier(.4,0,.2,1)',
          whiteSpace: 'nowrap',
        }}
        title="Chat with AI"
      >
        {open ? (
          <>
            <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}><X size={16} strokeWidth={2.2} /></span>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>Close</span>
          </>
        ) : (
          <>
            {/* Recruiter24x7 logo */}
            <BrandMark size={28} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: 0.2 }}>
              Chat with AI
            </span>
          </>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="r-chat-panel" style={{
          position: 'fixed', bottom: 86, right: 24, zIndex: 999,
          width: 380, height: 540,
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 16px',
            background: '#640d5f',
            borderBottom: '1px solid rgba(88,166,255,0.15)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Avatar with glow */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 12,
                  background: '#faf5ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 0 14px rgba(88,166,255,0.45)',
                }}><BrandMark size={26} /></div>
                {/* Online dot */}
                <span style={{
                  position: 'absolute', bottom: -2, right: -2,
                  width: 10, height: 10, borderRadius: '50%',
                  background: '#3fb950',
                  border: '2px solid #1a2744',
                  display: 'block',
                  animation: 'pulseOnline 2s ease-in-out infinite',
                }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13.5, color: '#fff', letterSpacing: 0.2 }}>
                  <BrandWordmark size={13.5} weight={700} color="inherit" /> Assistant
                </div>
                <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>
                  Powered by Claude · Always on
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {messages.length > 0 && (
                <button
                  onClick={clearChat}
                  style={{
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 6, cursor: 'pointer', color: 'rgba(255,255,255,0.45)',
                    fontSize: 11, padding: '3px 9px', fontWeight: 500,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}
                >
                  Clear
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                  cursor: 'pointer', color: 'rgba(255,255,255,0.5)',
                  fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
              ><X size={15} strokeWidth={2.2} /></button>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.length === 0 && (
              <div>
                <div style={{ textAlign: 'center', padding: '12px 0 16px', color: 'var(--text-muted)', fontSize: 12 }}>
                  Ask me to create, update, or manage jobs.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      style={{
                        textAlign: 'left', padding: '7px 11px', borderRadius: 8,
                        background: 'var(--bg-surface)', border: '1px solid var(--border)',
                        color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer',
                        transition: 'border-color 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '88%',
                  padding: '8px 12px',
                  borderRadius: m.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                  background: m.role === 'user'
                    ? 'linear-gradient(135deg, #58a6ff, #a78bfa)'
                    : 'var(--bg-surface)',
                  border: m.role === 'user' ? 'none' : '1px solid var(--border)',
                  color: m.role === 'user' ? '#fff' : 'var(--text-primary)',
                  fontSize: 13,
                  lineHeight: 1.5,
                }}>
                  {renderMarkdown(m.content)}
                </div>
                {m.action && (
                  <div style={{ maxWidth: '88%', width: '88%' }}>
                    <ActionCard action={m.action} onNavigate={handleNavigate} />
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  padding: '8px 14px', borderRadius: '12px 12px 12px 2px',
                  background: 'var(--bg-surface)', border: '1px solid var(--border)',
                  display: 'flex', gap: 4, alignItems: 'center',
                }}>
                  {[0, 1, 2].map(d => (
                    <span key={d} style={{
                      width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)',
                      display: 'inline-block',
                      animation: `bounce 1.2s ${d * 0.2}s infinite ease-in-out`,
                    }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '10px 12px',
            borderTop: '1px solid var(--border)',
            display: 'flex', gap: 8,
          }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
              placeholder="e.g. Post a Python developer job in Mohali…"
              style={{
                flex: 1, padding: '8px 12px', fontSize: 13,
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--text-primary)', outline: 'none',
              }}
              onFocus={e => e.target.style.borderColor = 'var(--accent)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              style={{
                padding: '8px 14px', borderRadius: 8, border: 'none',
                background: input.trim() && !loading ? 'var(--accent)' : 'var(--bg-hover)',
                color: input.trim() && !loading ? '#fff' : 'var(--text-muted)',
                cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                fontSize: 16, fontWeight: 700, transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            ><Send size={15} strokeWidth={2.2} /></button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
        @keyframes pulseOnline {
          0%, 100% { box-shadow: 0 0 0 0 rgba(63,185,80,0.5); }
          50% { box-shadow: 0 0 0 4px rgba(63,185,80,0); }
        }
      `}</style>
    </>
  );
}
